/***
 *
 *   APP LAYOUT
 *   Main application layout containing the navigation
 *   and header (title, secondary nav and signed in user)
 *
 *   PROPS
 *   title: title of the view
 *
 **********/

import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext, AppNav, PrimaryActionProvider, useFirebaseToken } from "components/lib";
import { useLocation } from "react-router-dom";
import { GlobalHeader } from "./GlobalHeader";
import { mensajeConfirmacion } from "components/mensajes";
import Style from "./app.module.scss";

function NavProgress() {
  const location = useLocation();
  const [active, setActive] = useState(false);

  useEffect(() => {
    setActive(true);
    const t = setTimeout(() => setActive(false), 500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: 3,
      zIndex: 9999, pointerEvents: 'none',
      background: active ? '#6366f1' : 'transparent',
      transformOrigin: 'left',
      transform: active ? 'scaleX(1)' : 'scaleX(0)',
      transition: active
        ? 'transform 0.45s cubic-bezier(0.4,0,0.2,1)'
        : 'opacity 0.2s ease, background 0.2s ease',
      opacity: active ? 1 : 0,
    }} />
  );
}

const IDLE_MS = 60 * 60 * 1000; // 1 hora

// Se muestra bajo el nombre en el menú: "Tu Ruta Segura / Contador v1.5"
const ROLE_LABELS = {
  master:      'Propietario',
  owner:       'Dueño',
  admin:       'Admin',
  coordinator: 'Coordinador',
  accounting:  'Contador',
  user:        'Usuario',
};

export function AppLayout(props) {
  const { notificationStatus, askPermission } = useFirebaseToken();
  const context = useContext(AuthContext);
  const [notifBannerDismissed, setNotifBannerDismissed] = useState(
    () => sessionStorage.getItem('notif_banner_dismissed') === '1'
  );
  const idleTimer = useRef(null);

  const isCoordinator = context.permission?.coordinator && !context.permission?.admin;
  const isAccounting = context.permission?.accounting && !context.permission?.admin;

  // Idle timeout: logout tras 1 hora sin actividad. Se avisa que la sesión
  // expiró, porque el usuario no pidió salir.
  useEffect(() => {
    const reset = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => context.signout(true), IDLE_MS);
    };
    const events = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(idleTimer.current);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [context]);

  // Permiso de notificaciones: un solo aviso al entrar, no una franja permanente.
  useEffect(() => {
    if (notifBannerDismissed || notificationStatus !== 'default') return;

    setNotifBannerDismissed(true);
    sessionStorage.setItem('notif_banner_dismissed', '1');

    mensajeConfirmacion(
      'Notificaciones',
      // El contador no opera rutas, así que a él no se le habla de rutas.
      isAccounting
        ? 'Actívalas para recibir alertas en tiempo real.'
        : 'Actívalas para recibir alertas de rutas en tiempo real.',
      'Activar',
      'Ahora no'
    ).then(confirmado => { if (confirmado) askPermission(); });
  }, [notificationStatus, notifBannerDismissed, askPermission, isAccounting]);

  const allItems = [
    { section: "INICIO" },
    { label: "Dashboard", icon: "activity", link: "/dashboard" },

    { section: "GESTIÓN" },
    { label: "Rutas", icon: "navigation", link: "/routes" },
    { label: "Monitoreo", icon: "crosshair", link: "/live-routes" },
    { label: "Conductores", icon: "users", link: "/drivers" },
    { label: "Vehículos", icon: "truck", link: "/vehicles" },
    { label: "Clientes", icon: "user-check", link: "/clients" },
    { label: "Reportes", icon: "clipboard", link: "/attendance" },

    { section: "CONFIGURACIÓN", adminOnly: true },
    { label: "Colegios", icon: "map-pin", link: "/schools", adminOnly: true },
    { label: "Periodos", icon: "calendar", link: "/periods", adminOnly: true },
    { label: "Cursos", icon: "book", link: "/courses", adminOnly: true },

    // El módulo contable no va en este menú: lo opera el rol contable y solo
    // aparece en su propio menú (accountingMenu). Desde acá el admin únicamente
    // da de alta contadores, en ADMINISTRACIÓN.

    { section: "ADMINISTRACIÓN", adminOnly: true },
    { label: "Coordinadores", icon: "shield", link: "/account/coordinators", adminOnly: true },
    { label: "Contadores", icon: "briefcase", link: "/account/accountants", adminOnly: true },

    { section: "SISTEMA" },
    { label: "Notificaciones", icon: "bell", link: "/notifications" },
    { label: "Ayuda", icon: "help-circle", link: "/help" },
    { label: "Mi cuenta", icon: "user", link: "/account", end: true },
  ];

  // Rol contable: solo su vista + sistema. Coordinador: sin items de admin.
  // Admin/master: todo lo operativo, sin el módulo contable.
  //
  // Menú del contador, ordenado por el ciclo del dinero: entra, sale, se
  // registra, se analiza y al final lo que se configura una vez.
  const accountingMenu = [
    { section: "INICIO" },
    { label: "Panel contable", icon: "activity", link: "/accounting/dashboard" },

    // Lo que entra: cobro a los representantes
    { section: "COBROS" },
    { label: "Facturación", icon: "file-text", link: "/accounting/billing" },
    { label: "Revisión de comprobantes", icon: "search", link: "/accounting/ocr-review" },
    { label: "Acreditación", icon: "credit-card", link: "/accounting/reconciliation" },

    // Lo que sale: compras y servicios de flota
    { section: "PAGOS" },
    { label: "Proveedores y gastos", icon: "package", link: "/accounting/suppliers" },

    // Donde queda registrado y de donde salen los informes
    { section: "LIBROS" },
    { label: "Asientos y libro diario", icon: "book-open", link: "/accounting/journal" },
    { label: "Reportes", icon: "bar-chart-2", link: "/accounting/reports" },

    // Se define una vez y casi no se vuelve a tocar
    { section: "CONFIGURACIÓN" },
    { label: "Plan de cuentas", icon: "list", link: "/accounting/chart-of-accounts" },
    { label: "Periodos contables", icon: "calendar", link: "/accounting/periods" },
    { label: "Parámetros", icon: "sliders", link: "/accounting/settings" },

    { section: "SISTEMA" },
    { label: "Ayuda", icon: "help-circle", link: "/help" },
    { label: "Mi cuenta", icon: "user", link: "/account", end: true },
  ];
  const navItems = isAccounting
    ? accountingMenu
    : allItems
      .filter((item) => !(isCoordinator && item.adminOnly))
      .map(({ adminOnly, ...rest }) => rest);

  return (
    <PrimaryActionProvider>
      <NavProgress />
      <AppNav
        roleLabel={ROLE_LABELS[context.user?.permission] ?? 'Panel'}
        items={navItems}
        bottomItems={[
          { label: "Cerrar Sesión", icon: "log-out", action: context.signout, danger: true },
        ]}
      />

      <main className={Style.app}>
        <GlobalHeader />

        <div className={Style.canvas}>

          <div>
            {<props.children {...props.data} />}
          </div>

        </div>
      </main>
    </PrimaryActionProvider>
  );
}
